import React, { useState, useEffect, useCallback, useRef } from "react";
import "./LocationOnboarding.css";
import {
  getCurrentPosition,
  reverseGeocodeLatLng,
  getLocationOnboardingState,
  setLocationOnboardingState,
  saveDeliveryLocationSnapshot,
  hasStoredDeliveryLocation,
} from "../../utils/locationHelper";

function shouldShowLocationOnboarding() {
  if (getLocationOnboardingState()) return false;
  if (hasStoredDeliveryLocation()) return false;
  return true;
}

/**
 * First-visit prompt: shortly after load we request browser location (permission dialog),
 * save area for checkout, and offer retry / dismiss if needed.
 */
export default function LocationOnboarding() {
  const dismissedEarlyRef = useRef(false);
  const inFlightRef = useRef(false);

  const [visible, setVisible] = useState(() => shouldShowLocationOnboarding());
  const [phase, setPhase] = useState("idle"); // idle | loading | denied | error
  const [hint, setHint] = useState("");

  const requestLocation = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setPhase("loading");
    setHint("");
    try {
      const { latitude, longitude } = await getCurrentPosition();
      const geo = await reverseGeocodeLatLng(latitude, longitude);
      saveDeliveryLocationSnapshot({
        lat: latitude,
        lng: longitude,
        pincode: geo.pincode,
        city: geo.city,
        state: geo.state,
        line1: geo.line1,
      });
      setLocationOnboardingState("done");
      setVisible(false);
    } catch (e) {
      const msg = e.message || "Could not get location.";
      if (msg.toLowerCase().includes("denied") || msg.toLowerCase().includes("permission")) {
        setPhase("denied");
      } else {
        setPhase("error");
      }
      setHint(msg);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (getLocationOnboardingState()) return;
    if (hasStoredDeliveryLocation()) {
      setLocationOnboardingState("done");
      setVisible(false);
      return;
    }

    const t = setTimeout(() => {
      if (dismissedEarlyRef.current) return;
      if (getLocationOnboardingState()) return;
      if (hasStoredDeliveryLocation()) return;
      if (inFlightRef.current) return;
      requestLocation();
    }, 500);

    return () => clearTimeout(t);
  }, [requestLocation]);

  const handleDismiss = () => {
    dismissedEarlyRef.current = true;
    setLocationOnboardingState("skipped");
    setVisible(false);
  };

  const handleRetry = () => {
    requestLocation();
  };

  if (!visible) return null;

  return (
    <div className="locationOnboarding" role="dialog" aria-labelledby="location-onboarding-title">
      <div className="locationOnboardingInner">
        <div className="locationOnboardingText">
          <h2 id="location-onboarding-title">Delivery location</h2>
          <p>
            We use your area for shipping estimates and to speed up checkout. Your browser may ask for permission now
            — you can also use the button below.
          </p>
          {phase === "denied" && (
            <p className="locationOnboardingWarning">
              Location was blocked. Enable it in your browser settings for this site, or enter your address later at
              checkout.
            </p>
          )}
          {phase === "error" && hint && <p className="locationOnboardingWarning">{hint}</p>}
          <p className="locationOnboardingOsm">Address lookup uses OpenStreetMap data (© OpenStreetMap contributors).</p>
        </div>
        <div className="locationOnboardingActions">
          <button
            type="button"
            className="locationOnboardingPrimary"
            onClick={handleRetry}
            disabled={phase === "loading"}
          >
            {phase === "loading" ? "Requesting…" : "Allow location"}
          </button>
          <button type="button" className="locationOnboardingSecondary" onClick={handleDismiss}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
